import { Helmet } from 'react-helmet-async';
import { pathnameWithTrailingSlash } from '../utils/pathNormalization';

const SITE_URL = 'https://trimergy.io';
const SITE_NAME = 'Trimergy';
const DEFAULT_IMAGE = `${SITE_URL}/logoDibujo.png`;
const DEFAULT_TITLE = 'Trimergy | Software de baterías industriales';

/**
 * SEOHead - Dynamic meta tags per page for proper Google indexation.
 *
 * `title` is used as-is (no "| Trimergy" suffix). Include the brand in the
 * string only when the SEO spec asks for it.
 *
 * @param {string} title - Final page title
 * @param {string} description - Meta description (max ~160 chars)
 * @param {string} path - URL path (e.g., "/pricing") - used for canonical
 * @param {boolean} noIndex - Set true for private/protected pages
 * @param {string} image - OG image URL (optional)
 * @param {string} type - OG type (default: "website")
 * @param {string} ogDescription - Open Graph description (defaults to description)
 * @param {string} twitterDescription - Twitter description (defaults to description)
 */
const SEOHead = ({
  title,
  description,
  path = '/',
  noIndex = false,
  image = DEFAULT_IMAGE,
  type = 'website',
  ogDescription,
  twitterDescription,
}) => {
  const fullTitle = title || DEFAULT_TITLE;
  const ogDesc = ogDescription || description;
  const twitterDesc = twitterDescription || description;
  const canonicalPath = pathnameWithTrailingSlash(path);
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={ogDesc} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={twitterDesc} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

export default SEOHead;
